import { useCallback, useState } from "react";
import { exportDashboardAsPdf } from "src/utils/pdfExport/exportDashboardAsPdf";

export type PdfExportStatus = "idle" | "generating" | "error";

export interface DashboardPdfExportParams {
  containerEl: HTMLElement | null;
  dashboardName: string;
  dateRange: { startDate: string; endDate: string };
  onBeforeCapture?: () => Promise<void>;
  onAfterCapture?: () => void;
}

export interface UseDashboardPdfExportResult {
  status: PdfExportStatus;
  errorMessage: string | null;
  exportPdf: (_params: DashboardPdfExportParams) => Promise<void>;
}

const GENERIC_ERROR_MESSAGE = "We couldn't generate the PDF. Please try again.";

export function useDashboardPdfExport(): UseDashboardPdfExportResult {
  const [status, setStatus] = useState<PdfExportStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const exportPdf = useCallback(async (params: DashboardPdfExportParams) => {
    if (status === "generating" || !params.containerEl) {
      return;
    }
    setStatus("generating");
    setErrorMessage(null);
    try {
      await params.onBeforeCapture?.();

      await new Promise<void>(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      await exportDashboardAsPdf({
        containerEl: params.containerEl,
        dashboardName: params.dashboardName,
        dateRange: params.dateRange,
      });
      setStatus("idle");
    } catch (error) {
      // eslint-disable-next-line no-console -- surfaces the real cause; the user-facing message stays generic.
      console.error("PDF export failed", error);
      setStatus("error");
      setErrorMessage(GENERIC_ERROR_MESSAGE);
    } finally {
      params.onAfterCapture?.();
    }
  }, [status]);

  return { status, errorMessage, exportPdf };
}
